import axios, { AxiosInstance, AxiosResponse } from "axios";
import { ReportFormData } from "../pages/SubmitReport";

class ApiServiceClass {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL:
        process.env.REACT_APP_API_BASE_URL ||
        "https://jsonplaceholder.typicode.com",
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Handle common errors
        if (error.response?.status === 401) {
          // Unauthorized - redirect to login or clear token
          localStorage.removeItem("authToken");
        }
        return Promise.reject(error);
      }
    );
  }

  // GET request
  async get<T = any>(url: string, params?: any): Promise<AxiosResponse<T>> {
    return this.api.get(url, { params });
  }

  // POST request
  async post<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.post(url, data);
  }

  // PUT request
  async put<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.put(url, data);
  }

  // DELETE request
  async delete<T = any>(url: string): Promise<AxiosResponse<T>> {
    return this.api.delete(url);
  }

  // PATCH request
  async patch<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data);
  }

  // Set auth token
  setAuthToken(token: string): void {
    localStorage.setItem("authToken", token);
  }

  // Clear auth token
  clearAuthToken(): void {
    localStorage.removeItem("authToken");
  }

  // Get current auth token
  getAuthToken(): string | null {
    return localStorage.getItem("authToken");
  }

  // Submit a report
  async submitReport(reportData: ReportFormData): Promise<any> {
    try {
      // Convert form data to FormData for file upload support
      const formData = new FormData();

      // Add text fields
      formData.append("title", reportData.title || "");
      formData.append("description", reportData.description || "");
      formData.append("priority", reportData.priority || "medium");
      formData.append("issueType", reportData.issueType || "road_damage");
      formData.append("userId", reportData.userId?.toString() || "");

      // Add location data
      if (reportData.location) {
        formData.append(
          "latitude",
          reportData.location.latitude?.toString() || ""
        );
        formData.append(
          "longitude",
          reportData.location.longitude?.toString() || ""
        );
      }

      // Add image file if present
      if (reportData.images && Array.isArray(reportData.images)) {
        reportData.images.forEach((image, index) => {
          formData.append(`images[${index}]`, image);
        });
      }

      // Add timestamp
      formData.append("timestamp", new Date().toISOString());

      // Make API call to submit report
      const response = await this.api.post("/report", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 30000, // Extended timeout for file upload
      });

      return response;
    } catch (error: any) {
      // Handle API errors gracefully
      console.error("Error submitting report:", error);

      // If it's a network error or API is not available,
      // fall back to local storage for offline support
      if (error.code === "NETWORK_ERROR" || error.response?.status >= 500) {
        return this.saveReportLocally(reportData);
      }

      // For other errors, throw them to be handled by the calling component
      throw error;
    }
  }

  // Fallback method to save report locally when API is unavailable
  private async saveReportLocally(reportData: any): Promise<any> {
    try {
      // Get existing offline reports
      const existingReports = JSON.parse(
        localStorage.getItem("offlineReports") || "[]"
      );

      // Create new report with offline flag
      const offlineReport = {
        id: `offline_${Date.now()}`,
        ...reportData,
        status: "offline_pending",
        timestamp: new Date().toISOString(),
        isOffline: true,
      };

      // Add to offline reports
      existingReports.push(offlineReport);
      localStorage.setItem("offlineReports", JSON.stringify(existingReports));

      return {
        data: {
          id: offlineReport.id,
          status: "offline_pending",
          message:
            "Report saved locally. Will sync when connection is restored.",
          isOffline: true,
        },
      };
    } catch (error) {
      throw new Error("Failed to save report offline");
    }
  }

  // Method to sync offline reports when connection is restored
  async syncOfflineReports(): Promise<void> {
    try {
      const offlineReports = JSON.parse(
        localStorage.getItem("offlineReports") || "[]"
      );

      if (offlineReports.length === 0) return;

      const syncPromises = offlineReports.map(async (report: any) => {
        try {
          const { isOffline, ...reportData } = report;
          await this.submitReport(reportData);
          return report.id;
        } catch (error) {
          console.error(`Failed to sync report ${report.id}:`, error);
          return null;
        }
      });

      const syncedIds = await Promise.all(syncPromises);
      const failedReports = offlineReports.filter(
        (report: any, index: number) => syncedIds[index] === null
      );

      // Update localStorage with only failed reports
      localStorage.setItem("offlineReports", JSON.stringify(failedReports));
    } catch (error) {
      console.error("Error syncing offline reports:", error);
    }
  }
}

export const ApiService = new ApiServiceClass();
